// src/components/TodoList.tsx

import React, { useState } from "react";

interface Todo {
  id: number;
  task: string;
  is_done: boolean;
}

interface TodoListProps {
  todos: Todo[];
  date: string;
  onAdd: (task: string) => void;
  onToggle: (id: number, currentStatus: boolean) => void;
  onDelete: (id: number) => void;
  onEdit: (id: number, newTask: string) => void; 
}

const TodoList = ({ todos, date, onAdd, onToggle, onDelete, onEdit }: TodoListProps) => {
  const [inputText, setInputText] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim()) return;
    onAdd(inputText);
    setInputText("");
  };

  const handleEditSave = (id: number) => {
    if (!editingText.trim()) return;
    onEdit(id, editingText);
    setEditingId(null); 
  };
  
  const handleEditCancel = () => {
    setEditingId(null); 
  };


  const themeColor = "#a5668b";
  const buttonStyle: React.CSSProperties = {
    backgroundColor: themeColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '5px 10px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    minWidth: '55px', 
  };

  // 텍스트 한 줄 강제 스타일
  const textOverflowStyle: React.CSSProperties = {
    whiteSpace: "nowrap", 
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1, 
    cursor: "pointer"
  };

  const completedStyle = { 
    ...textOverflowStyle,
    textDecoration: "line-through", 
    color: "#aaa", 
  };
  const incompleteStyle = { 
    ...textOverflowStyle,
  };


  // 단일 Todo 아이템을 렌더링하는 함수
  const renderTodoItem = (todo: Todo) => (
    <li
      key={todo.id}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "12px 0",
        borderBottom: "1px solid #f0f0f0",
        gap: "10px",
        opacity: todo.is_done ? 0.6 : 1, 
      }}
    >
      {/* 1. 체크박스 (토글 기능) */}
      <input
        type="checkbox"
        checked={todo.is_done}
        onChange={() => onToggle(todo.id, todo.is_done)} 
        style={{ 
            width: "18px", 
            height: "18px", 
            cursor: "pointer",
            accentColor: themeColor 
        }}
      />

      {/* 2. 수정 모드 vs 보기 모드 */}
      {editingId === todo.id ? (
        // 2-1. 수정 모드: Input 필드와 '저장' 버튼
        <>
          <input
            type="text"
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            
            // ★ onBlur 제거: Enter 또는 버튼 클릭으로만 저장 (안정성 확보)
            
            onKeyDown={(e) => { 
                if (e.key === 'Enter') {
                    handleEditSave(todo.id);
                }
                if (e.key === 'Escape') { 
                    handleEditCancel();
                }
            }}
            style={{ 
              flex: 1, 
              padding: "5px", 
              border: `1px solid ${themeColor}`, 
              borderRadius: "4px",
              ...textOverflowStyle, 
            }}
            autoFocus 
          />
          {/* 저장 버튼 (클릭 이벤트 전파 중단) */}
          <button
            onClick={(e) => {
                e.stopPropagation(); // 이벤트 전파 중단
                handleEditSave(todo.id);
            }}
            style={buttonStyle}
          >
            저장
          </button>
        </>
      ) : (
        // 2-2. 보기 모드: 텍스트 Span과 '삭제' 버튼
        <>
            <span
              onClick={() => { // 클릭 시 수정 모드 활성화
                setEditingId(todo.id);
                setEditingText(todo.task);
              }}
              style={todo.is_done ? completedStyle : incompleteStyle}
            >
              {todo.task}
            </span>

            {/* 삭제 버튼 */}
            <button
              onClick={() => onDelete(todo.id)}
              style={{
                backgroundColor: "transparent",
                border: "1px solid #ddd",
                borderRadius: "4px",
                padding: "2px 8px",
                fontSize: "0.8rem",
                cursor: "pointer",
                color: "#666",
                minWidth: '55px', 
              }}
            >
              삭제
            </button>
        </>
      )}
    </li>
  );


  return (
    <div>
      <div style={{ marginBottom: "10px" }}>
        <div style={{ fontSize: "13px", color: "#888" }}>
          {date}
        </div>
        <h3 style={{ margin: "4px 0 0" }}>할 일 목록</h3>
    </div>

      {/* 입력 폼 */}
      <form onSubmit={handleSubmit} style={{ display: "flex", gap: "10px", marginBottom: "30px" }}>
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="할 일을 입력하세요"
          style={{
            flex: 1,
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            outline: "none",
          }}
        />
        <button
          type="submit"
          style={{
            padding: "10px 20px",
            borderRadius: "5px",
            border: "none",
            backgroundColor: themeColor,
            color: "white",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          추가
        </button>
      </form>

      {/* 리스트 출력 */}
      {todos.length === 0 && (
      <div
        style={{
          margin: "200px 0",
          textAlign: "center",
          color: "#9a8f98",
          fontSize: "14px",
          lineHeight: "1.6",
        }}
      >
          오늘의 작은 목표를 하나 적어보세요:D
        </div>
      )}
      <ul style={{ listStyle: "none", padding: 0 }}>
        {/* 미완료 항목 먼저 렌더링 */}
        {todos.filter((t) => !t.is_done).map(renderTodoItem)}

        {/* 완료된 항목은 아래로 렌더링 */}
        {todos.filter((t) => t.is_done).map(renderTodoItem)}
      </ul>
    </div>
  );
};

export default TodoList;